import {
	BlogArticleBySlugQuery,
	BlogArticleBySlugWithDraftsQuery,
	BlogCategoryQuery,
	BlogLandingQuery,
	BlogsByCategoryQuery
} from '@sanity/graphql/queries/blog-education-center';
import { mapInvestorUpdateArticleDetailPage, mapInvestorUpdateArticleTeasers } from '@utils/blog';
import {
	SanityArticle,
	SanityArticleResponse,
	SanityArticleTeaser,
	SanityBlogCategory,
	SanityBlogCategoryResponse,
	SanityBlogLanding,
	SanityBlogLandingResponse
} from 'types/sanity/education-center';
import { useQueryWithContext, villusClient } from '@utils/villus';
import { flattenSharedFooterDisclaimers } from '@utils/sanity';
import { FooterDisclaimerItem } from 'types/sanity';
import { RouteLocationNormalizedLoaded } from 'vue-router';
import { useQuery } from 'villus';

export async function getBlogCategories(): Promise<SanityBlogCategory[]> {
	const { data } = await useQuery<SanityBlogCategoryResponse>({
		client: villusClient,
		query: BlogCategoryQuery,
		tags: ['all_blog_categories']
	}).execute();

	return data?.allBlogCategory ?? [];
}

export async function getCategoryBySlug(categorySlug: string): Promise<SanityBlogCategory | null> {
	const { data } = await useQuery<SanityBlogCategoryResponse>({
		client: villusClient,
		query: BlogCategoryQuery,
		tags: ['category_by_slug'],
		variables: { categorySlug: [categorySlug] }
	}).execute();

	return data?.allBlogCategory?.[0] ?? null;
}

export async function getBlogArticlesByCategory(
	categorySlug: string,
	limit?: number,
	context?: string
): Promise<SanityArticleTeaser[]> {
	const { data } = await useQueryWithContext<SanityArticleResponse>({
		query: BlogsByCategoryQuery,
		baseTag: 'articles_by_category',
		contextTag: context,
		variables: { categorySlug, limit }
	}).execute();

	return mapInvestorUpdateArticleTeasers(data?.allArticle ?? []);
}

export async function getSanityBlogArticle(
	route: RouteLocationNormalizedLoaded,
	context?: string
): Promise<SanityArticle | null> {
	const slug = route.params.blogPostUrlSlug as string;
	const isSanityPreviewRoute = route.meta.isSanityPreview;

	const { data } = await useQueryWithContext<SanityArticleResponse>({
		query: isSanityPreviewRoute ? BlogArticleBySlugWithDraftsQuery : BlogArticleBySlugQuery,
		variables: { slug },
		baseTag: 'blog_article_by_slug',
		contextTag: context
	}).execute();

	const article = data?.allArticle?.[0];

	const responseFooterDisclaimers = article?.footerDisclaimers as FooterDisclaimerItem[];
	if (article && responseFooterDisclaimers?.length > 0) {
		article.flatFooterDisclaimers = flattenSharedFooterDisclaimers(responseFooterDisclaimers);
	}

	return article ? mapInvestorUpdateArticleDetailPage(article) : null;
}

export async function getSanityBlogLanding(): Promise<SanityBlogLanding | null> {
	const { data } = await useQuery<SanityBlogLandingResponse>({
		client: villusClient,
		query: BlogLandingQuery,
		tags: ['blog_landing']
	}).execute();

	return data?.allBlogLanding?.[0] ?? null;
}
